import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Radio from '@mui/material/Radio';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ChangeEvent, FC } from 'react';

import { useTickets } from 'contexts/TicketsContext';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import {
  AgeFilterValuesE,
  AssigneeFilterValuesE,
  TicketsRefinementKeysE,
} from 'declarations/enums/tickets';
import type { RoofTopI } from 'declarations/interfaces/roofTop';
import type { TicketRequestCategoryI } from 'declarations/interfaces/ticketRequestCategories';
import type { TicketRequestSubcategoryI } from 'declarations/interfaces/ticketRequestSubcategories';
import type { NoopT } from 'declarations/types/common';
import { useFetchTicketRequestCategories } from 'hooks/api/useTicketRequestCategories';
import {
  useFetchTicketRequestSubcategories,
  useFetchTicketRequestSubcategoriesByCategoryIds,
} from 'hooks/api/useTicketSubcategories';

type TicketsFiltersDropdownPropsT = {
  anchorEl: HTMLElement | null;
  isOpen: boolean;
  onClose: () => void;
  onToggleFilter: (key: TicketsRefinementKeysE, value: string) => NoopT;
  shouldFiltersReset: number;
};

type SectionKeyT = 'dealers' | 'ticketAge' | 'other' | 'assignee' | 'categories' | 'subcategories';

type SectionT = {
  key: SectionKeyT;
  label: string;
  visible: boolean;
};

const LEFT_PANEL_WIDTH = 225;

export const TicketsFiltersDropdown: FC<TicketsFiltersDropdownPropsT> = ({
  anchorEl,
  isOpen,
  onClose,
  onToggleFilter,
  shouldFiltersReset,
}): JSX.Element | null => {
  const {
    activeTab,
    getSearchParam,
    roofTopsList,
    selectedRoofTop,
    updateSearchParam,
    updateSearchParamsArray,
    clearSearchParam,
  } = useTickets();

  const { data: categories } = useFetchTicketRequestCategories(true, selectedRoofTop);

  const selectedCategoryIdsNum = useMemo((): number[] => {
    const raw = getSearchParam(TicketsRefinementKeysE.Category) || '';
    return raw
      .split(',')
      .filter(Boolean)
      .map((val): number => Number(val))
      .filter((id): boolean => !isNaN(id));
  }, [getSearchParam]);

  const subCategoriesResult = useFetchTicketRequestSubcategories({
    isEnabled: activeTab === ActiveTabTypeE.Schedule,
    roofTopId: undefined,
  });

  const ticketSubCategoriesByCategoryResult = useFetchTicketRequestSubcategoriesByCategoryIds({
    isEnabled: activeTab === ActiveTabTypeE.Tickets && selectedCategoryIdsNum.length > 0,
    categoryIds: selectedCategoryIdsNum,
    roofTopId: selectedRoofTop,
  });

  const subCategoryParamRaw = getSearchParam(TicketsRefinementKeysE.SubCategory) || '';
  const requestSubCategoryParamRaw =
    getSearchParam(TicketsRefinementKeysE.RequestSubCategoryIds) || '';

  const selectedSubCategoryIds = useMemo((): number[] => {
    if (subCategoryParamRaw === 'All') return [];
    return subCategoryParamRaw
      .split(',')
      .filter(Boolean)
      .map((val): number => Number(val))
      .filter((id): boolean => !isNaN(id));
  }, [subCategoryParamRaw]);

  const selectedRequestSubCategoryIds = useMemo((): number[] => {
    return requestSubCategoryParamRaw
      .split(',')
      .filter(Boolean)
      .map((val): number => Number(val))
      .filter((id): boolean => !isNaN(id));
  }, [requestSubCategoryParamRaw]);

  const isSubCategoryAllSelected = subCategoryParamRaw === 'All';

  const [activeSection, setActiveSection] = useState<SectionKeyT | null>(null);
  const [dealerSearch, setDealerSearch] = useState<string>('');

  useEffect((): void => {
    setActiveSection(null);
    setDealerSearch('');
  }, [shouldFiltersReset]);

  useEffect((): void => {
    if (!isOpen) {
      setActiveSection(null);
      setDealerSearch('');
    }
  }, [isOpen]);

  const sections = useMemo((): SectionT[] => {
    return [
      { key: 'dealers' as const, label: 'Dealers', visible: true },
      { key: 'ticketAge' as const, label: 'Ticket Age', visible: true },
      { key: 'other' as const, label: 'Other', visible: true },
      { key: 'assignee' as const, label: 'Assignee', visible: true },
      {
        key: 'categories' as const,
        label: 'Categories',
        visible: activeTab === ActiveTabTypeE.Tickets,
      },
      { key: 'subcategories' as const, label: 'Subcategories', visible: true },
    ];
  }, [activeTab]);

  const filteredDealers = useMemo((): RoofTopI[] => {
    if (!roofTopsList) return [];
    if (!dealerSearch.trim()) return roofTopsList;
    const term = dealerSearch.toLowerCase();
    return roofTopsList.filter((r): boolean => r.name.toLowerCase().includes(term));
  }, [roofTopsList, dealerSearch]);

  const currentRoofTopId = getSearchParam(TicketsRefinementKeysE.RoofTopId);

  const handleDealerSelect = useCallback(
    (rooftopId: string | null): void => {
      clearSearchParam([
        TicketsRefinementKeysE.RoofTopId,
        TicketsRefinementKeysE.Category,
        TicketsRefinementKeysE.SubCategory,
        TicketsRefinementKeysE.RequestSubCategoryIds,
      ]);

      if (rooftopId) {
        updateSearchParam(TicketsRefinementKeysE.RoofTopId, rooftopId);
      }
    },
    [clearSearchParam, updateSearchParam]
  );

  const visibleCategories = useMemo((): TicketRequestCategoryI[] => {
    if (!categories) return [];
    return categories.filter((c): boolean => c.categoryName.toLowerCase() !== 'schedule');
  }, [categories]);

  const selectedCategoryIds = useMemo((): Set<string> => {
    const raw = getSearchParam(TicketsRefinementKeysE.Category);
    if (!raw) return new Set<string>();
    return new Set(raw.split(',').filter(Boolean));
  }, [getSearchParam]);

  const handleCategoryToggle = useCallback(
    (categoryId: number): void => {
      const idStr = String(categoryId);
      const current = new Set(selectedCategoryIds);

      if (current.has(idStr)) {
        current.delete(idStr);
      } else {
        current.add(idStr);
      }

      const ids = Array.from(current);
      updateSearchParamsArray(TicketsRefinementKeysE.Category, ids.length > 0 ? ids : null);
    },
    [selectedCategoryIds, updateSearchParamsArray]
  );

  const handleSubCategoryAll = useCallback((): void => {
    if (isSubCategoryAllSelected) {
      updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, null);
    } else {
      updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, ['All']);
    }
  }, [isSubCategoryAllSelected, updateSearchParamsArray]);

  const handleToggleSubCategory = useCallback(
    (subCategoryId: number): void => {
      const allIds = subCategoriesResult.data?.map((s): number => s.id) ?? [];
      let next: number[];
      if (isSubCategoryAllSelected) {
        next = allIds.filter((id): boolean => id !== subCategoryId);
      } else {
        const currentIndex = selectedSubCategoryIds.indexOf(subCategoryId);
        next = [...selectedSubCategoryIds];
        if (currentIndex === -1) {
          next.push(subCategoryId);
        } else {
          next.splice(currentIndex, 1);
        }
      }
      if (next.length === 0) {
        updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, null);
      } else if (next.length === allIds.length) {
        updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, ['All']);
      } else {
        updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, next);
      }
    },
    [
      isSubCategoryAllSelected,
      selectedSubCategoryIds,
      subCategoriesResult.data,
      updateSearchParamsArray,
    ]
  );

  const handleRequestSubCategoryAll = useCallback((): void => {
    const allIds = ticketSubCategoriesByCategoryResult.data?.map((s): number => s.id) ?? [];
    if (selectedRequestSubCategoryIds.length === allIds.length) {
      updateSearchParamsArray(TicketsRefinementKeysE.RequestSubCategoryIds, null);
    } else {
      updateSearchParamsArray(TicketsRefinementKeysE.RequestSubCategoryIds, allIds);
    }
  }, [
    selectedRequestSubCategoryIds,
    ticketSubCategoriesByCategoryResult.data,
    updateSearchParamsArray,
  ]);

  const handleToggleRequestSubCategory = useCallback(
    (subCategoryId: number): void => {
      const currentIndex = selectedRequestSubCategoryIds.indexOf(subCategoryId);
      const next = [...selectedRequestSubCategoryIds];
      if (currentIndex === -1) {
        next.push(subCategoryId);
      } else {
        next.splice(currentIndex, 1);
      }
      updateSearchParamsArray(
        TicketsRefinementKeysE.RequestSubCategoryIds,
        next.length > 0 ? next : null
      );
    },
    [selectedRequestSubCategoryIds, updateSearchParamsArray]
  );

  const handleBack = (): void => {
    setActiveSection(null);
    setDealerSearch('');
  };

  const handleDealerSearchChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setDealerSearch(e.target.value);
  };

  const clearDealerSearch = (): void => {
    setDealerSearch('');
  };

  const isChecked = useCallback(
    (key: TicketsRefinementKeysE, value: string): boolean => {
      const param = getSearchParam(key);
      if (!param) return false;
      return param.split(',').includes(value);
    },
    [getSearchParam]
  );

  if (!isOpen || !anchorEl) return null;

  const renderDealersContent = (): JSX.Element => (
    <Box>
      <TextField
        size="small"
        fullWidth
        placeholder="Search dealers..."
        value={dealerSearch}
        onChange={handleDealerSearchChange}
        sx={{ px: 1, pt: 1, pb: 0.5 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: dealerSearch ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={clearDealerSearch}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
      <Divider sx={{ mt: 0.5 }} />
      <Box>
        <MenuItem onClick={(): void => handleDealerSelect(null)}>
          <ListItemIcon>
            <Radio checked={!currentRoofTopId} size="small" />
          </ListItemIcon>
          <ListItemText primary="All" />
        </MenuItem>
        {filteredDealers.map(
          (dealer): JSX.Element => (
            <MenuItem key={dealer.id} onClick={(): void => handleDealerSelect(dealer.id)}>
              <ListItemIcon>
                <Radio checked={currentRoofTopId === dealer.id} size="small" />
              </ListItemIcon>
              <ListItemText primary={dealer.name} />
            </MenuItem>
          )
        )}
      </Box>
    </Box>
  );

  const renderTicketAgeContent = (): JSX.Element => (
    <Box>
      <MenuItem
        onClick={onToggleFilter(TicketsRefinementKeysE.TicketAge, AgeFilterValuesE.Under24Hours)}
      >
        <ListItemIcon>
          <Checkbox
            checked={isChecked(TicketsRefinementKeysE.TicketAge, AgeFilterValuesE.Under24Hours)}
            size="small"
          />
        </ListItemIcon>
        <ListItemText primary="Under 24h" />
      </MenuItem>
      <MenuItem
        onClick={onToggleFilter(TicketsRefinementKeysE.TicketAge, AgeFilterValuesE.Over24Hours)}
      >
        <ListItemIcon>
          <Checkbox
            checked={isChecked(TicketsRefinementKeysE.TicketAge, AgeFilterValuesE.Over24Hours)}
            size="small"
          />
        </ListItemIcon>
        <ListItemText primary="Over 24h" />
      </MenuItem>
    </Box>
  );

  const renderOtherContent = (): JSX.Element => (
    <Box>
      <MenuItem onClick={onToggleFilter(TicketsRefinementKeysE.Past30Days, 'true')}>
        <ListItemIcon>
          <Checkbox checked={isChecked(TicketsRefinementKeysE.Past30Days, 'true')} size="small" />
        </ListItemIcon>
        <ListItemText primary="Past 30 Days" />
      </MenuItem>
      <MenuItem onClick={onToggleFilter(TicketsRefinementKeysE.PastMonth, 'true')}>
        <ListItemIcon>
          <Checkbox checked={isChecked(TicketsRefinementKeysE.PastMonth, 'true')} size="small" />
        </ListItemIcon>
        <ListItemText primary="Past Month" />
      </MenuItem>
      <MenuItem onClick={onToggleFilter(TicketsRefinementKeysE.IsArchived, 'true')}>
        <ListItemIcon>
          <Checkbox checked={isChecked(TicketsRefinementKeysE.IsArchived, 'true')} size="small" />
        </ListItemIcon>
        <ListItemText primary="Archived" />
      </MenuItem>
    </Box>
  );

  const renderAssigneeContent = (): JSX.Element => (
    <Box>
      <MenuItem
        onClick={onToggleFilter(TicketsRefinementKeysE.Assignee, AssigneeFilterValuesE.Dealer)}
      >
        <ListItemIcon>
          <Checkbox
            checked={isChecked(TicketsRefinementKeysE.Assignee, AssigneeFilterValuesE.Dealer)}
            size="small"
          />
        </ListItemIcon>
        <ListItemText primary="Dealer" />
      </MenuItem>
      <MenuItem
        onClick={onToggleFilter(TicketsRefinementKeysE.Assignee, AssigneeFilterValuesE.WarrCloud)}
      >
        <ListItemIcon>
          <Checkbox
            checked={isChecked(TicketsRefinementKeysE.Assignee, AssigneeFilterValuesE.WarrCloud)}
            size="small"
          />
        </ListItemIcon>
        <ListItemText primary="WarrCloud" />
      </MenuItem>
      <MenuItem
        onClick={onToggleFilter(TicketsRefinementKeysE.Assignee, AssigneeFilterValuesE.OEM)}
      >
        <ListItemIcon>
          <Checkbox
            checked={isChecked(TicketsRefinementKeysE.Assignee, AssigneeFilterValuesE.OEM)}
            size="small"
          />
        </ListItemIcon>
        <ListItemText primary="OEM" />
      </MenuItem>
    </Box>
  );

  const renderCategoriesContent = (): JSX.Element => {
    if (!selectedRoofTop) {
      return (
        <Box sx={{ px: 2, py: 1 }}>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            Select a dealership to enable categories.
          </Typography>
        </Box>
      );
    }
    return (
      <Box>
        <MenuItem
          onClick={(): void => {
            updateSearchParamsArray(TicketsRefinementKeysE.Category, null);
          }}
        >
          <ListItemIcon>
            <Checkbox checked={selectedCategoryIds.size === 0} size="small" />
          </ListItemIcon>
          <ListItemText primary="All" />
        </MenuItem>
        {visibleCategories.map(
          (cat): JSX.Element => (
            <MenuItem key={cat.id} onClick={(): void => handleCategoryToggle(cat.id)}>
              <ListItemIcon>
                <Checkbox checked={selectedCategoryIds.has(String(cat.id))} size="small" />
              </ListItemIcon>
              <ListItemText primary={cat.categoryName} />
            </MenuItem>
          )
        )}
      </Box>
    );
  };

  const renderSubcategoriesContent = (): JSX.Element => {
    if (activeTab === ActiveTabTypeE.Tickets) {
      if (selectedCategoryIdsNum.length === 0) {
        return (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              Select at least one category to filter by subcategory.
            </Typography>
          </Box>
        );
      }
      const ticketSubs = ticketSubCategoriesByCategoryResult.data ?? [];
      const isRequestSubCategoryAllSelected =
        ticketSubs.length > 0 && selectedRequestSubCategoryIds.length === ticketSubs.length;
      return (
        <Box>
          <MenuItem onClick={handleRequestSubCategoryAll}>
            <ListItemIcon>
              <Checkbox
                checked={isRequestSubCategoryAllSelected}
                indeterminate={
                  !isRequestSubCategoryAllSelected && selectedRequestSubCategoryIds.length > 0
                }
                size="small"
              />
            </ListItemIcon>
            <ListItemText primary="All" />
          </MenuItem>
          {ticketSubCategoriesByCategoryResult.isLoading && (
            <Typography sx={{ fontSize: 12, color: 'text.secondary', px: 2, py: 0.5 }}>
              Loading subcategories...
            </Typography>
          )}
          {ticketSubs.map(
            (sub: TicketRequestSubcategoryI): JSX.Element => (
              <MenuItem key={sub.id} onClick={(): void => handleToggleRequestSubCategory(sub.id)}>
                <ListItemIcon>
                  <Checkbox checked={selectedRequestSubCategoryIds.includes(sub.id)} size="small" />
                </ListItemIcon>
                <ListItemText primary={sub.name} />
              </MenuItem>
            )
          )}
        </Box>
      );
    }
    return (
      <Box>
        <MenuItem onClick={handleSubCategoryAll}>
          <ListItemIcon>
            <Checkbox
              checked={isSubCategoryAllSelected}
              indeterminate={!isSubCategoryAllSelected && selectedSubCategoryIds.length > 0}
              size="small"
            />
          </ListItemIcon>
          <ListItemText primary="All" />
        </MenuItem>
        {subCategoriesResult.isLoading && (
          <Typography sx={{ fontSize: 12, color: 'text.secondary', px: 2, py: 0.5 }}>
            Loading subcategories...
          </Typography>
        )}
        {subCategoriesResult.data?.map(
          (sub: TicketRequestSubcategoryI): JSX.Element => (
            <MenuItem key={sub.id} onClick={(): void => handleToggleSubCategory(sub.id)}>
              <ListItemIcon>
                <Checkbox
                  checked={isSubCategoryAllSelected || selectedSubCategoryIds.includes(sub.id)}
                  size="small"
                />
              </ListItemIcon>
              <ListItemText primary={sub.name} />
            </MenuItem>
          )
        )}
      </Box>
    );
  };

  const renderSectionContent = (): JSX.Element | null => {
    switch (activeSection) {
      case 'dealers':
        return renderDealersContent();
      case 'ticketAge':
        return renderTicketAgeContent();
      case 'other':
        return renderOtherContent();
      case 'assignee':
        return renderAssigneeContent();
      case 'categories':
        return renderCategoriesContent();
      case 'subcategories':
        return renderSubcategoriesContent();
      default:
        return null;
    }
  };

  const activeSectionLabel = sections.find((s): boolean => s.key === activeSection)?.label ?? '';

  return (
    <Popper open={isOpen} anchorEl={anchorEl} placement="bottom-start" sx={{ zIndex: 1300 }}>
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          elevation={8}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ width: LEFT_PANEL_WIDTH, minHeight: 200 }}>
            <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5, pb: 1, fontWeight: 600 }}>
              Filters
            </Typography>
            <Divider />
            {sections
              .filter((s): boolean => s.visible)
              .map(
                (section): JSX.Element => (
                  <MenuItem
                    key={section.key}
                    selected={activeSection === section.key}
                    onClick={(): void => setActiveSection(section.key)}
                    sx={{ justifyContent: 'space-between' }}
                  >
                    <ListItemText primary={section.label} />
                    <ChevronRightIcon fontSize="small" color="action" />
                  </MenuItem>
                )
              )}
          </Box>

          {activeSection && (
            <Box
              sx={{
                width: 'max-content',
                minWidth: 280,
                maxWidth: 480,
                maxHeight: 420,
                overflowY: 'auto',
                overflowX: 'hidden',
                borderLeft: (theme): string => `1px solid ${theme.palette.divider}`,
                minHeight: 200,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', px: 1, pt: 1, pb: 0.5 }}>
                <IconButton size="small" onClick={handleBack} sx={{ mr: 0.5 }}>
                  <KeyboardBackspaceIcon fontSize="small" />
                </IconButton>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {activeSectionLabel}
                </Typography>
              </Box>
              <Divider />
              {renderSectionContent()}
            </Box>
          )}
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};
